import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { menuService } from '../utils/api';
import DeleteMenuModal from '../components/DeleteMenuModal';
import { useSchool } from '../contexts/SchoolContext';
import { useUser } from '../contexts/UserContext';
import { useUrl } from '../contexts/UrlContext';
import { useMenus, useRecentMenus } from '../hooks/useMenu';
import { useMenuTypes } from '../hooks/useMenuTypes';
import { useUserSchools } from '../hooks/useSchool';
import LoadingSpinner from '../components/LoadingSpinner';

const AdminDashboard = () => {
  // --- Hooks Moved to Top ---
  const { t, i18n } = useTranslation();
  const { school } = useSchool();
  const { user, refreshUser } = useUser();
  const { getAdminUrl, getMenuEditorUrl } = useUrl();
  const location = useLocation();
  const navigate = useNavigate();
  
  // Data Fetching Hooks
  const { 
    data: recentMenus = [], 
    isLoading: loading, 
    error: queryError,
    refetch: refetchRecentMenus
  } = useRecentMenus(school?.id);
  const {
    data: menuTypes = [],
    isLoading: menuTypesLoading,
    error: menuTypesError,
    refetch: refetchMenuTypes
  } = useMenuTypes(school?.id, {
    refetchOnMount: true,
    staleTime: 0,
  });
  const { data: userSchools = [], isLoading: schoolsLoading } = useUserSchools();
  
  // State Hooks
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedMenuId, setSelectedMenuId] = useState(null);
  const [highestRole, setHighestRole] = useState('None');
  const [roleChecked, setRoleChecked] = useState(false);
  
  // --- Calculate userRolesForSchool BEFORE useEffects that use it ---
  const currentSchoolData = userSchools?.find(s => s.id === school?.id);
  const userRolesForSchool = currentSchoolData?.roles || [];
  // -----------------------------------------------------------------

  // --- Moved useEffects/useCallbacks Start ---
  // Debug logging for menu types
  useEffect(() => {
    console.log('[DEBUG AdminDashboard] Menu types from React Query:', menuTypes);
    
    // Force a fresh refetch on first load and language changes
    if (school?.id) {
      console.log('[DEBUG AdminDashboard] Forcing a refresh of menu types');
      refetchMenuTypes();
    }
  }, [menuTypes.length, i18n.language, school?.id, refetchMenuTypes]);
  
  // Debug logging for user/school
  useEffect(() => {
    console.log('[DEBUG AdminDashboard] Current user:', user);
    console.log('[DEBUG AdminDashboard] Current school from context:', school);
    
    if (user?.schools && school) {
      // Check if user has this school in their list
      const userHasSchool = user.schools.some(s => s.id === school.id);
      console.log('[DEBUG AdminDashboard] User has current school:', userHasSchool);
      
      if (userHasSchool) {
        // Find school in user's schools to get roles
        const userSchool = user.schools.find(s => s.id === school.id);
        console.log('[DEBUG AdminDashboard] User roles for this school:', userSchool?.roles);
      }
    }
    
    // Check for newly created school
    const lastCreatedSchool = sessionStorage.getItem('lastCreatedSchool');
    if (lastCreatedSchool && school) {
      try {
        const schoolInfo = JSON.parse(lastCreatedSchool);
        console.log('[DEBUG AdminDashboard] Found last created school data:', schoolInfo);
        
        if (schoolInfo.subdomain === school.subdomain) {
          console.log('[DEBUG AdminDashboard] Current school matches last created school');
          
          // Compare stored roles with current roles
          const userRolesForSchoolCheck = user?.schools
            ?.find(s => s.id === school?.id)?.roles || [];
          
          console.log('[DEBUG AdminDashboard] Stored roles for new school:', schoolInfo.userRoles);
          console.log('[DEBUG AdminDashboard] Current roles from user data:', userRolesForSchoolCheck);
          console.log('[DEBUG AdminDashboard] Roles match?', 
            JSON.stringify(schoolInfo.userRoles) === JSON.stringify(userRolesForSchoolCheck));
        }
      } catch (e) {
        console.error('[DEBUG AdminDashboard] Error parsing lastCreatedSchool:', e);
      }
    }
  }, [user, school]);
  
  // Determine highest role
  useEffect(() => {
    if (userRolesForSchool.length > 0) {
      let determinedRole = 'None';
      if (userRolesForSchool.includes('Superadmin')) determinedRole = 'Superadmin';
      else if (userRolesForSchool.includes('Admin')) determinedRole = 'Admin';
      else if (userRolesForSchool.includes('Editor')) determinedRole = 'Editor';
      else if (userRolesForSchool.includes('Viewer')) determinedRole = 'Viewer';
      setHighestRole(determinedRole);
      setRoleChecked(true);
      console.log(`[AdminDashboard] Determined highest role: ${determinedRole}`);
    }
  }, [userRolesForSchool]);

  // Log settings button visibility
  useEffect(() => {
    // Determine admin access within this hook to avoid dependency issues
    const isAdmin = userRolesForSchool.includes('Admin');
    const isSuperadmin = userRolesForSchool.includes('Superadmin');
    const canAccessSettings = isAdmin || isSuperadmin;
    console.log('[DEBUG AdminDashboard] Settings button visibility:', canAccessSettings);
    
    // Force refresh user data if the roles don't match expected values
    // Check if this is a newly created school
    const lastCreatedSchool = sessionStorage.getItem('lastCreatedSchool');
    if (lastCreatedSchool && !canAccessSettings) {
      try {
        const schoolInfo = JSON.parse(lastCreatedSchool);
        const isRecentlyCreated = (Date.now() - schoolInfo.timestamp) < 300000; // 5 minutes
        
        if (isRecentlyCreated && schoolInfo.subdomain === school?.subdomain && 
            schoolInfo.userRoles?.includes('Superadmin')) {
          console.log('[DEBUG AdminDashboard] Newly created school missing Superadmin permission!');
          console.log('[DEBUG AdminDashboard] Expected roles:', schoolInfo.userRoles);
          console.log('[DEBUG AdminDashboard] Actual roles:', userRolesForSchool);
          
          // Since this is a critical permission issue, we should refresh the user data
          console.log('[DEBUG AdminDashboard] Forcing user data refresh to fix permission issue');
          refreshUser().then(() => {
            console.log('[DEBUG AdminDashboard] User data refreshed after permission mismatch');
          });
        }
      } catch (e) {
        console.error('[DEBUG AdminDashboard] Error handling permission refresh:', e);
      }
    }
  }, [school, refreshUser, userRolesForSchool]);

  // Clean URL params
  useEffect(() => {
    // If we're on the dashboard but not in a school context, ensure URL is clean
    if (!school && window.location.search && window.location.search.includes('schoolId=')) {
      console.log('[AdminDashboard] Cleaning URL parameters on dashboard page with no school context');
      try {
        const url = new URL(window.location.href);
        const params = new URLSearchParams(url.search);
        params.delete('schoolId');
        
        const newSearch = params.toString();
        const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '');
        
        // Use history API directly
        window.history.replaceState({}, '', newUrl);
      } catch (error) {
        console.error('[AdminDashboard] Error cleaning URL parameters:', error);
      }
    }
  }, [school]);
  // --- Moved useEffects End ---

  // Set error from query error
  const error = queryError ? 'Failed to load menus. Please try again later.' : null;

  // Find the current school in the fresh schools data
  // const currentSchoolData = userSchools?.find(s => s.id === school?.id); // MOVED UP
  // const userRolesForSchool = currentSchoolData?.roles || []; // MOVED UP
  
  // ---- Conditional Returns ----
  // Show loading spinner while checking roles or fetching school data
  if (schoolsLoading || !roleChecked) {
    return (
      <>
        <Navbar isAdmin={true} />
        <div className="flex justify-center items-center h-64">
          <LoadingSpinner />
        </div>
      </>
    );
  }

  // Perform redirect if the user is only a Viewer
  if (highestRole === 'Viewer') {
    console.log('[AdminDashboard] User is Viewer, redirecting to preview...');
    return <Navigate to="preview" replace />;
  }
  // ---- End Role Check ----
  
  // These role checks now use the fresh data from useUserSchools
  const isAdmin = userRolesForSchool.includes('Admin');
  const isSuperadmin = userRolesForSchool.includes('Superadmin');
  const canAccessSettings = isAdmin || isSuperadmin;
  
  console.log('[DEBUG AdminDashboard] Role check results from useUserSchools:', {
    userRolesForSchool,
    isAdmin,
    isSuperadmin,
    canAccessSettings,
    schoolId: school?.id,
    subdomain: school?.subdomain,
    currentSchoolData
  });
  
  // Use the adminUrl with the current subdomain
  const adminUrl = getAdminUrl();
  
  // Apply menu type translations to the menu data
  const translatedMenus = recentMenus.map(menu => {
    // Clone the menu to avoid mutating the original
    const translatedMenu = { ...menu };
    
    // Only translate the menu_type if we have menuTypes available
    if (menuTypes.length > 0 && menu.menu_type_id) {
      // Find the corresponding menu type by ID
      const menuType = menuTypes.find(mt => mt.id === menu.menu_type_id);
      
      // If found, use its properly translated name
      if (menuType) {
        translatedMenu.menu_type = menuType.name;
      }
    }
    
    return translatedMenu;
  });
  
  // Format month name using i18n locale
  const formatMonthYear = (year, monthNumber) => {
    if (!year || !monthNumber) return t('general.unknownDate');
    
    try {
      // Create a date for the given month and year
      const date = new Date(year, monthNumber - 1, 1);
      
      // Use the current i18n language to format the date
      let formattedDate = date.toLocaleDateString(i18n.language, { 
        month: 'long', 
        year: 'numeric' 
      });
      
      // Capitalize the first letter of the month name
      // This ensures consistent capitalization across all languages
      // First isolate the month name - it's typically before any spaces or the year
      const firstSpace = formattedDate.indexOf(' ');
      if (firstSpace > 0) {
        // If there's a space, capitalize just the part before it
        const monthPart = formattedDate.substring(0, firstSpace);
        const restPart = formattedDate.substring(firstSpace);
        formattedDate = monthPart.charAt(0).toUpperCase() + monthPart.slice(1) + restPart;
      } else {
        // If no space, just capitalize the first letter of the whole string
        formattedDate = formattedDate.charAt(0).toUpperCase() + formattedDate.slice(1);
      }
      
      return formattedDate;
    } catch (err) {
      console.error('Error formatting date:', err);
      return `${monthNumber}/${year}`;
    }
  };
  
  const handleOpenDeleteModal = (menuId) => {
    setSelectedMenuId(menuId);
    setIsDeleteModalOpen(true);
  };
  
  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedMenuId(null);
  };
  
  const handleDeleteSuccess = () => {
    // Immediately remove the deleted menu from the UI for instant feedback
    refetchRecentMenus();
  };
  
  if (!school) {
    return (
      <>
        <Navbar isAdmin={true} />
        <div className="max-w-7xl mx-auto p-4">
          <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded">
            {t('admin.noSchoolSelected', 'No school selected. Please select a school from the dashboard.')}
          </div>
        </div>
      </>
    );
  }
  
  if (loading || menuTypesLoading) return (
    <>
      <Navbar isAdmin={true} />
      <div className="max-w-7xl mx-auto p-4">
        <div className="flex justify-center py-8">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
        </div>
      </div>
    </>
  );
  
  if (error) return (
    <>
      <Navbar isAdmin={true} />
      <div className="max-w-7xl mx-auto p-4">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    </>
  );
  
  return (
    <>
      <Navbar isAdmin={true} />
      <div className="max-w-7xl mx-auto p-4">
        <h1 className="text-3xl font-bold mb-6">{t('admin.dashboard')}</h1>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Link 
            to={`${adminUrl}/menu-editor`}
            className="bg-blue-100 p-6 rounded-lg shadow hover:shadow-md transition-shadow"
          >
            <h2 className="text-xl font-semibold text-blue-700">{t('admin.menuEditor')}</h2>
            <p className="mt-2 text-gray-600">{t('admin.menuEditorDesc')}</p>
          </Link>
          
          <Link 
            to={`${adminUrl}/translations`}
            className="bg-green-100 p-6 rounded-lg shadow hover:shadow-md transition-shadow"
          >
            <h2 className="text-xl font-semibold text-green-700">{t('admin.translationEditor')}</h2>
            <p className="mt-2 text-gray-600">{t('admin.translationEditorDesc')}</p>
          </Link>
          
          <Link 
            to={`${adminUrl}/translation-queue`} 
            className="bg-yellow-100 p-6 rounded-lg shadow hover:shadow-md transition-shadow"
          >
            <h2 className="text-xl font-semibold text-yellow-700">{t('admin.translationQueue')}</h2>
            <p className="mt-2 text-gray-600">{t('admin.translationQueueDesc')}</p>
          </Link>

          {/* Only show nutrient queue if school has show_nutrients enabled */}
          {school?.show_nutrients && (
            <Link 
              to={`${adminUrl}/nutrient-queue`} 
              className="bg-red-100 p-6 rounded-lg shadow hover:shadow-md transition-shadow"
            >
              <h2 className="text-xl font-semibold text-red-700">{t('admin.nutrientQueue')}</h2>
              <p className="mt-2 text-gray-600">{t('admin.nutrientQueueDesc')}</p>
            </Link>
          )}

          {/* Only show tag queue if school has show_tags enabled */}
          {school?.show_tags && (
            <Link 
              to={`${adminUrl}/tag-queue`} 
              className="bg-orange-100 p-6 rounded-lg shadow hover:shadow-md transition-shadow"
            >
              <h2 className="text-xl font-semibold text-orange-700">{t('admin.tagQueue')}</h2>
              <p className="mt-2 text-gray-600">{t('admin.tagQueueDesc')}</p>
            </Link>
          )}
          
          {canAccessSettings && (
            <Link 
              to={`${adminUrl}/settings`}
              className="bg-purple-100 p-6 rounded-lg shadow hover:shadow-md transition-shadow"
            >
              <h2 className="text-xl font-semibold text-purple-700">{t('admin.settings')}</h2>
              <p className="mt-2 text-gray-600">{t('admin.settingsDesc')}</p>
            </Link>
          )}
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-semibold mb-4">{t('admin.recentMenus')}</h2>
          
          {translatedMenus.length === 0 ? (
            <p className="text-gray-500">{t('admin.noMenusFound')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full table-fixed">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="py-2 px-4 text-left w-1/3">{t('admin.month')}</th>
                    <th className="py-2 px-4 text-left w-1/3">{t('admin.menuType')}</th>
                    <th className="py-2 px-4 text-left w-1/3">{t('admin.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {translatedMenus.map(menu => (
                    <tr key={menu.id} className="border-t">
                      <td className="py-2 px-4 truncate">
                        {formatMonthYear(menu.year, menu.month)}
                      </td>
                      <td className="py-2 px-4 truncate">{menu.menu_type}</td>
                      <td className="py-2 px-4">
                        <div className="flex justify-start items-center space-x-4">
                          <Link 
                            to={`${adminUrl}/menu-editor/${menu.id}`}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            {t('admin.edit')}
                          </Link>
                          <button
                            onClick={() => handleOpenDeleteModal(menu.id)}
                            className="text-red-600 hover:text-red-800"
                          >
                            {t('admin.delete')}
                          </button>
                          <Link 
                            to={`${adminUrl}/preview/${menu.id}`}
                            className="text-green-600 hover:text-green-800"
                          >
                            {t('admin.preview')}
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
      
      {isDeleteModalOpen && (
        <DeleteMenuModal
          isOpen={isDeleteModalOpen}
          menuId={selectedMenuId}
          onClose={handleCloseDeleteModal}
          onSuccess={handleDeleteSuccess}
        />
      )}
    </>
  );
};

export default AdminDashboard; 